import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { ArrowLeft, Moon, Sun, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import GradientLoginLayout from '@/components/GradientLoginLayout';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  // Get school_code from URL if on tenant login page
  const schoolCode = localStorage.getItem('school_code') || null;

  useEffect(() => {
    if (user) {
      navigateToDashboard(user.role);
    }
  }, [user]);

  const navigateToDashboard = (role) => {
    if (role === 'super_admin') {
      navigate('/super-admin');
    } else if (role === 'school_admin') {
      navigate('/admin');
    } else if (role === 'staff') {
      navigate('/staff-dashboard');
    } else {
      toast.error('Invalid credentials for Admin Portal');
      navigate('/auth');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Pass school_code and expected_role for validation
      const userData = await login(email, password, schoolCode, 'admin');
      // Allow Super Admin, School Admin, and Staff to login via Admin Portal
      if (userData.role !== 'super_admin' && userData.role !== 'school_admin' && userData.role !== 'staff') {
        toast.error('This portal is for Administrators and Staff only. Please use the correct portal.');
        return;
      }
      navigateToDashboard(userData.role);
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GradientLoginLayout portalType="admin">
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute top-4 right-4"
      >
        {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-xl border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm" data-testid="admin-login-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Admin & Staff Login</CardTitle>
            <CardDescription>Enter your credentials to access the dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@school.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email-input"
                  required
                  className="bg-white dark:bg-slate-900"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-password-input"
                    required
                    className="bg-white dark:bg-slate-900 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
              
              <div className="text-center">
                <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary">
                  Forgot your password?
                </Link>
              </div>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/auth" 
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                data-testid="back-to-portals"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Portal Selection
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Need help? Contact the Super Administrator
        </p>
      </motion.div>
    </GradientLoginLayout>
  );
};

export default AdminLoginPage;
